import { ogImageMeta } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import logo from "@/assets/thevibingco_logo.svg.asset.json";
import { MagneticButton } from "@/components/magnetic-button";
import { SiteFooter } from "@/components/site-footer";

export const Route = createFileRoute("/hiring")({
  head: () => ({
    meta: [
      { title: "Hiring · the Vibing Company® — Designer-engineers wanted" },
      {
        name: "description",
        content:
          "We're hiring forward-deployed designers who ship in Cursor, Claude Code, Codex and Lovable. Taste, speed, initiative. Apply: jon@vibing.inc.",
      },
      { property: "og:title", content: "Hiring · the Vibing Company®" },
      ...ogImageMeta("hiring", "Share card: designer-engineers wanted"),
      {
        property: "og:description",
        content:
          "Forward-deployed designers who blur the line between design and engineering. Cursor, Claude Code, Codex, Lovable.",
      },
      { property: "og:url", content: "https://vibing.inc/hiring" },
    ],
    links: [{ rel: "canonical", href: "https://vibing.inc/hiring" }],
  }),
  component: Hiring,
});

const TOOLS = [
  { name: "Cursor", note: "Pair-programming IDE", glyph: "⌘" },
  { name: "Claude Code", note: "Agentic terminal", glyph: "◆" },
  { name: "Codex", note: "OpenAI coding agent", glyph: "▲" },
  { name: "Lovable", note: "Where we live", glyph: "❤" },
];

const PRINCIPLES = [
  {
    n: "01",
    title: "Taste over templates.",
    body: "You can tell why one layout works and the other dies. You'd rather ship one beautiful screen than three default ones.",
  },
  {
    n: "02",
    title: "Speed is a feature.",
    body: "A working draft beats a perfect plan. You move from blank file to shipped page in hours, not weeks.",
  },
  {
    n: "03",
    title: "AI-native, not AI-curious.",
    body: "You think in agents. You delegate to Cursor and Claude the way a director talks to a crew. You read diffs like prose.",
  },
  {
    n: "04",
    title: "Initiative is the job.",
    body: "Nobody hands you a ticket. You find the broken thing, propose the fix, and ship it before standup ends.",
  },
];

const LOOPS = [
  { k: "Mon", v: "Pick up a real client project. Read the codebase. Write the plan." },
  { k: "Tue", v: "Ship the first slice. Show the client. Adjust." },
  { k: "Wed", v: "Cloud migration, accessibility pass, or full UI rebuild. Your call." },
  { k: "Thu", v: "Pair with Jon on the hardest hour of the week." },
  { k: "Fri", v: "Demo. Invoice. Close out. Drink something cold." },
];

function Hiring() {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <main id="main" tabIndex={-1}>
      <Hero />
      <Role />
      <Tools />
      <Principles />
      <Week />
      <Apply />
      </main>
      <SiteFooter />
    </div>
  );
}


function Hero() {
  return (
    <section
      aria-labelledby="hiring-h"
      className="relative mx-auto max-w-[1400px] px-6 md:px-10 pt-[clamp(9rem,18vh,13rem)] pb-16 md:pb-24"
    >
      <p className="font-mono text-[11px] tracking-[0.24em] uppercase text-muted-foreground">
        Now hiring · 2026 · Remote / Stockholm / Penzberg
      </p>

      <h1
        id="hiring-h"
        className="mt-8 font-display tracking-[-0.035em] leading-[1.02]"
        style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}
      >
        We are not hiring
        <br />
        <span className="italic text-muted-foreground">regular designers.</span>
      </h1>

      <div className="mt-12 md:mt-16 grid md:grid-cols-12 gap-10 items-end">
        <p className="md:col-span-7 text-xl md:text-2xl leading-snug text-foreground max-w-[42ch]">
          We're looking for forward-deployed{" "}
          <span className="italic">designer-engineers</span>. People who sketch in Figma at noon and merge a pull request by four. Comfortable in Cursor, Claude Code, Codex, and obviously in Lovable.
        </p>
        <div className="md:col-span-4 md:col-start-9 flex flex-col gap-3">
          <MagneticButton href="mailto:jon@vibing.inc?subject=I want to vibe with you" variant="dark" immersive>
            Apply by email <span aria-hidden>→</span>
          </MagneticButton>
          <a
            href="#role"
            className="font-mono text-xs tracking-[0.18em] uppercase text-muted-foreground hover:text-foreground transition"
          >
            ↓ Read the role first
          </a>
        </div>
      </div>
    </section>
  );
}

function Role() {
  return (
    <section
      id="role"
      aria-labelledby="role-h"
      className="relative border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-32"
    >
      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-muted-foreground">
            01 / The role
          </p>
          <h2 id="role-h" className="mt-6 fluid-h2 font-display max-w-[12ch]">
            Forward-deployed designer.
          </h2>
        </div>

        <div className="md:col-span-7 md:col-start-6 space-y-8">
          <p className="text-xl md:text-2xl leading-snug">
            You sit on the same side of the table as the client. You read their stuck Lovable project, decide what should exist, and build it. Design and code are one motion.
          </p>
          <p className="text-lg text-muted-foreground leading-relaxed max-w-prose">
            One day you're rebuilding a checkout flow. The next, migrating their backend off Lovable Cloud onto their own Supabase. The week after, redrawing a hero from scratch because the original one was fine and fine is the enemy.
          </p>

          <ul className="border-t border-border divide-y divide-border">
            {[
              "Take a client project from messy to shipped, end to end.",
              "Design in browser. Tailwind, motion, semantic HTML. No mockups that lie.",
              "Pair with Jon and a tiny senior team. Receive feedback like a gift.",
              "Own outcomes, not tickets. If the client wins, you win.",
            ].map((item, i) => (
              <li key={item} className="flex items-baseline gap-6 py-5">
                <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-muted-foreground shrink-0">
                  R/{String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-base md:text-lg">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function Tools() {
  return (
    <section
      aria-labelledby="tools-h"
      className="relative border-t border-border/60 bg-foreground text-background overflow-hidden"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-32">
        <div className="flex items-end justify-between gap-6 mb-12 md:mb-16">
          <div>
            <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-background/50">
              02 / Tool stack
            </p>
            <h2 id="tools-h" className="mt-6 fluid-h2 font-display max-w-[16ch]">
              You speak four languages fluently.
            </h2>
          </div>
          <p className="hidden md:block font-mono text-[11px] tracking-[0.22em] uppercase text-background/70 pb-2">
            Required · daily use
          </p>
        </div>

        <ul className="grid grid-cols-1 md:grid-cols-4 border-t border-background/20">
          {TOOLS.map((t, i) => (
            <li
              key={t.name}
              className={`relative py-10 md:py-14 px-2 md:px-6 ${
                i > 0 ? "md:border-l border-background/20 border-t md:border-t-0" : ""
              }`}
            >
              <div
                className="font-display text-electric leading-none"
                style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}
              >
                {t.glyph}
              </div>
              <p
                className="mt-6 font-display leading-none tracking-[-0.03em]"
                style={{ fontSize: "clamp(1.75rem, 2.4vw, 2.5rem)" }}
              >
                {t.name}
              </p>
              <p className="mt-3 font-mono text-[11px] tracking-[0.18em] uppercase text-background/50">
                {t.note}
              </p>
            </li>
          ))}
        </ul>

        <p className="mt-16 text-lg text-background/70 max-w-prose">
          If you've never opened a terminal, this isn't the role. If you have your own Cursor rules file and a Claude Code playbook, keep reading.
        </p>
      </div>
    </section>
  );
}

function Principles() {
  return (
    <section
      aria-labelledby="principles-h"
      className="relative border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-32"
    >
      <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-muted-foreground">
        03 / What we look for
      </p>
      <h2 id="principles-h" className="mt-6 fluid-h2 font-display max-w-[18ch]">
        Four things. Non-negotiable.
      </h2>

      <ul className="mt-14 grid md:grid-cols-2 gap-px bg-border border border-border">
        {PRINCIPLES.map((p) => (
          <li key={p.n} className="bg-background p-8 md:p-12">
            <div className="flex items-baseline gap-5">
              <span
                className="font-display text-electric leading-none"
                style={{ fontSize: "clamp(2.5rem, 4vw, 4rem)" }}
              >
                {p.n}
              </span>
              <h3
                className="font-display tracking-[-0.03em] leading-[1.02]"
                style={{ fontSize: "clamp(1.5rem, 2.2vw, 2.25rem)" }}
              >
                {p.title}
              </h3>
            </div>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-snug max-w-[44ch]">
              {p.body}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Week() {
  return (
    <section
      aria-labelledby="week-h"
      className="relative border-t border-border/60 bg-secondary/40"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-32 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-muted-foreground">
            04 / A week with us
          </p>
          <h2 id="week-h" className="mt-6 fluid-h2 font-display max-w-[12ch]">
            A real five days.
          </h2>
          <p className="mt-6 text-muted-foreground max-w-prose">
            Not a perk page. Not a ping-pong table. A working week, the way it actually goes.
          </p>
        </div>

        <ul className="md:col-span-7 md:col-start-6 border-t border-foreground/20">
          {LOOPS.map((l, i) => (
            <motion.li
              key={l.k}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
              className="grid grid-cols-[80px_1fr] md:grid-cols-[120px_1fr] gap-6 items-baseline py-6 border-b border-foreground/20"
            >
              <span
                className="font-display tracking-[-0.03em]"
                style={{ fontSize: "clamp(1.75rem, 2.6vw, 2.5rem)" }}
              >
                {l.k}
              </span>
              <span className="text-base md:text-lg leading-snug">{l.v}</span>
            </motion.li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Apply() {
  return (
    <section
      id="apply"
      aria-labelledby="apply-h"
      className="relative border-t border-border/60 bg-foreground text-background"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-40 grid md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-7">
          <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-background/50">
            05 / Apply
          </p>
          <h2
            id="apply-h"
            className="mt-6 font-display tracking-[-0.035em] leading-[1.02]"
            style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}
          >
            Send the email.
          </h2>
          <p className="mt-8 text-lg md:text-xl text-background/70 max-w-[52ch] leading-snug">
            No form. No portal. Write something honest. Include three links to things you actually shipped, and tell us how you'd use Cursor, Claude Code, Codex or Lovable to fix one of them today.
          </p>
        </div>

        <div className="md:col-span-4 md:col-start-9 space-y-6">
          <a
            href="mailto:jon@vibing.inc?subject=Hiring · I want to vibe with you&body=Hi Jon,%0D%0A%0D%0AHere's what I shipped:%0D%0A1.%0D%0A2.%0D%0A3.%0D%0A%0D%0AHere's how I'd use [tool] to rebuild project [X]:%0D%0A%0D%0A"
            className="block group"
          >
            <span className="block font-mono text-[10px] tracking-[0.22em] uppercase text-background/50">
              [ ✉ ] Apply to
            </span>
            <span
              className="mt-3 block font-display text-electric-bright break-all"
              style={{ fontSize: "clamp(1.75rem, 3vw, 2.75rem)" }}
            >
              jon@vibing.inc
              <span aria-hidden className="inline-block ml-2 transition-transform group-hover:translate-x-2">↗</span>
            </span>
          </a>

          <ul className="border-t border-background/15 pt-6 space-y-3 text-sm text-background/70">
            <li className="flex gap-3"><span aria-hidden className="text-electric-bright">+</span>Three links to work, not a CV.</li>
            <li className="flex gap-3"><span aria-hidden className="text-electric-bright">+</span>One sentence on why most agencies bore you.</li>
            <li className="flex gap-3"><span aria-hidden className="text-electric-bright">+</span>We reply within 5 working days. Always.</li>
          </ul>
        </div>
      </div>
    </section>
  );
}
